import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getContentEntityTypeByName,
  getAllContentEntityTypes,
  getContentEntityTypeLookups,
  getContentEntityTypeNavigations,
  getSelectSortFactors,
  createContentEntityTypeMultilingual,
  editContentEntityTypeMultilingual,
  getContentEntityTypeByIdMultilingual,
  getContentEntityTypesDatatable,
} from './thunks';
import { ContentEntityTypeState } from './types';

export const name = 'contentEntityTypes';

const initialState: ContentEntityTypeState = {
  data: null,
  lookups: [],
  details: null,
  modalDetails: null,
  status: 'idle',
  updatingStatus: 'idle',
  modalStatus: 'idle',
  navigations: {
    data: [],
    status: 'idle',
  },
  selectSortFactors: {
    data: null,
    status: 'idle',
  },
  createMultilingual: {
    data: null,
    status: 'idle',
  },
  editMultilingual: {
    data: null,
    status: 'idle',
  },
  byIdMultilingual: {
    data: null,
    status: 'idle',
  },
  datatable: {
    data: null,
    status: 'idle',
  },
};

export const getAllContentEntityTypesAsync = createAsyncThunk(
  name + '/getAllContentEntityTypes',
  getAllContentEntityTypes
);

export const getContentEntityTypeLookupsAsync = createAsyncThunk(
  name + '/getContentEntityTypeLookups',
  getContentEntityTypeLookups
);

export const getContentEntityTypeByNameAsync = createAsyncThunk(
  name + '/getContentEntityTypeByName',
  getContentEntityTypeByName
);

export const getContentEntityTypeByNameForModalAsync = createAsyncThunk(
  name + '/getContentEntityTypeByNameForModal',
  getContentEntityTypeByName
);

export const getContentEntityTypeNavigationsAsync = createAsyncThunk(
  name + '/getContentEntityTypeNavigations',
  getContentEntityTypeNavigations
);

export const getSelectSortFactorsAsync = createAsyncThunk(
  name + '/getSelectSortFactors',
  getSelectSortFactors
);

export const createContentEntityMultilingualAsync = createAsyncThunk(
  name + '/createContentEntityMultilingual',
  createContentEntityTypeMultilingual
);

export const editContentEntityMultilingualAsync = createAsyncThunk(
  name + '/editContentEntityMultilingual',
  editContentEntityTypeMultilingual
);

export const getContentEntityTypeByIdMultilingualAsync = createAsyncThunk(
  name + '/getContentEntityTypeByIdMultilingual',
  getContentEntityTypeByIdMultilingual
);

export const getContentEntityTypesDatatableAsync = createAsyncThunk(
  name + '/getContentEntityTypesDatatable',
  getContentEntityTypesDatatable
);

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    clearDetails: (state) => {
      state.details = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllContentEntityTypesAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getAllContentEntityTypesAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.data = action.payload;
      })
      .addCase(getAllContentEntityTypesAsync.rejected, (state, action) => {
        state.status = 'failed';
      })
      .addCase(getContentEntityTypeLookupsAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getContentEntityTypeLookupsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.lookups = action.payload;
      })
      .addCase(getContentEntityTypeLookupsAsync.rejected, (state, action) => {
        state.status = 'failed';
      })
      .addCase(getContentEntityTypeByNameAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getContentEntityTypeByNameAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.details = action.payload;
      })
      .addCase(getContentEntityTypeByNameAsync.rejected, (state, action) => {
        state.status = 'failed';
      })
      .addCase(getContentEntityTypeByNameForModalAsync.pending, (state) => {
        state.modalStatus = 'loading';
      })
      .addCase(
        getContentEntityTypeByNameForModalAsync.fulfilled,
        (state, action) => {
          state.modalStatus = 'idle';
          state.modalDetails = action.payload;
        }
      )
      .addCase(getContentEntityTypeByNameForModalAsync.rejected, (state) => {
        state.modalStatus = 'failed';
      })
      .addCase(getContentEntityTypeNavigationsAsync.pending, (state) => {
        state.navigations.status = 'loading';
      })
      .addCase(
        getContentEntityTypeNavigationsAsync.fulfilled,
        (state, action) => {
          state.navigations = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getContentEntityTypeNavigationsAsync.rejected, (state) => {
        state.navigations.status = 'failed';
      })
      .addCase(getSelectSortFactorsAsync.pending, (state) => {
        state.selectSortFactors.status = 'loading';
      })
      .addCase(getSelectSortFactorsAsync.fulfilled, (state, action) => {
        state.selectSortFactors = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getSelectSortFactorsAsync.rejected, (state) => {
        state.selectSortFactors.status = 'failed';
      })
      .addCase(createContentEntityMultilingualAsync.pending, (state) => {
        state.createMultilingual.status = 'loading';
      })
      .addCase(
        createContentEntityMultilingualAsync.fulfilled,
        (state, action) => {
          state.createMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(createContentEntityMultilingualAsync.rejected, (state) => {
        state.createMultilingual.status = 'failed';
      })
      .addCase(editContentEntityMultilingualAsync.pending, (state) => {
        state.editMultilingual.status = 'loading';
      })
      .addCase(
        editContentEntityMultilingualAsync.fulfilled,
        (state, action) => {
          state.editMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(editContentEntityMultilingualAsync.rejected, (state) => {
        state.editMultilingual.status = 'failed';
      })
      .addCase(getContentEntityTypeByIdMultilingualAsync.pending, (state) => {
        state.byIdMultilingual.status = 'loading';
      })
      .addCase(
        getContentEntityTypeByIdMultilingualAsync.fulfilled,
        (state, action) => {
          state.byIdMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getContentEntityTypeByIdMultilingualAsync.rejected, (state) => {
        state.byIdMultilingual.status = 'failed';
      })
      .addCase(getContentEntityTypesDatatableAsync.pending, (state) => {
        state.datatable.status = 'loading';
      })
      .addCase(
        getContentEntityTypesDatatableAsync.fulfilled,
        (state, action) => {
          state.datatable = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getContentEntityTypesDatatableAsync.rejected, (state) => {
        state.datatable.status = 'failed';
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
